import DeviceCropTimeline from "@/app/activity/components/deviceTimeline/deviceCropTimeline/DeviceCropTimeline"
import SessionCropTimeline from "@/app/activity/components/timeline/sessionCrop/Timeline.tsx"
import ActivityDeviceInfo from "@/app/activity/detail/activityInfo/ActivityInfo"
import DevicePlayerInfo from "@/app/activity/detail/devicePlayerInfo/DevicePlayerInfo"
import {
  ActivityCropModeProps,
  CropType,
  LocalSessionCropData,
  LocalSessionCropHistory,
} from "@/app/activity/detail/type"
import { createSessionStore } from "@/app/activity/store"

import { Session, SessionData } from "@/types/session"
import classNames from "classnames"
import { add, sub } from "date-fns"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { usePlayerGroupsQuery, usePlayersQuery } from "@/hooks/api/useSettingQuery"

import OverlayScrollbar from "simplebar-react"
import { ActivityDataTimelineProps } from "@/types/activity.ts"
import SessionCropTaskBar from "@/app/activity/detail/sessionCropTaskBar/SessionCropTaskBar.tsx"
import CropDeleteConfirmDialog from "@/app/activity/detail/cropDeleteConfirmDialog/CropDeleteConfirmDialog"
import useRouter, { RouterEvent } from "@/hooks/router/useRoute"
import { useTranslation } from "react-i18next"

export default function ActivityCropMode({
  activityInfo,
  sessionList,
  middleOfScrollBarSetter,
  activityTimelineContRef,

  sessionDataList,
  playerSessionDataMap,
  groupedActivityData: groupedActivityDataList,
  teamDeviceList,
  playerList,
  localSessionCropHistory,
  setLocalSessionCropHistory,
  tempSessionCropHistory,
  setTempSessionCropHistory,
}: ActivityCropModeProps) {
  const playerInfoRef = useRef<HTMLDivElement>(null)
  const isDirty = useRef<boolean>(false)

  const router = useRouter()

  const { t } = useTranslation("")

  const [isCopying, setIsCopying] = useState<boolean>(false)
  const [lastActionType, setLastActionType] = useState<CropType | undefined>(undefined)
  const [deleteHistory, setDeleteHistory] = useState<LocalSessionCropHistory | undefined>(undefined)

  const { setSession, selectedSession } = createSessionStore((state) => ({
    setSession: state.setSession,
    selectedSession: state.session,
    setUpsertDialogOpen: state.setUpsertDialogOpen,
    isUpsertDialogOpen: state.upsertDialogOpen,
  }))

  const { data: playerGroups } = usePlayerGroupsQuery()
  const { data: allPlayers } = usePlayersQuery()

  const getPlayerGroupName = useCallback(
    (idfPlayer: number) => {
      if (!allPlayers || !playerGroups?.teamPlayerGroups) return undefined
      const playerSetting = allPlayers.find((p) => p.idfPlayer === idfPlayer)
      if (!playerSetting?.idfPlayerGroup) return undefined
      return playerGroups.teamPlayerGroups.find(
        (g) => g.idfTeamPlayerGroup === playerSetting.idfPlayerGroup,
      )?.name
    },
    [allPlayers, playerGroups],
  )

  const getPlayerPosition = useCallback(
    (idfPlayer: number, currentPosition?: string) => {
      const participatedPosition = playerList?.find((p) => p.idfPlayer === idfPlayer)?.position
      if (participatedPosition) return participatedPosition
      if (currentPosition) return currentPosition
      return allPlayers?.find((p) => p.idfPlayer === idfPlayer)?.position ?? ""
    },
    [allPlayers, playerList],
  )

  const { sessionDateStart, sessionDateFinish } = useMemo(() => {
    const sessionDateStart = sub(new Date(activityInfo?.dateStart || ""), { minutes: 15 })
    const sessionDateFinish = add(new Date(activityInfo?.dateFinish || ""), { minutes: 15 })

    return {
      sessionDateStart,
      sessionDateFinish,
    }
  }, [activityInfo])

  const groupedActivityData = useMemo(() => {
    if (!sessionDataList) return []

    const matchedToSession = sessionDataList
      .map((item) => item?.filter((session) => session.idfSession === selectedSession?.idfSession))
      .filter((item) => item && item?.length > 0)
      .flat()

    return groupedActivityDataList.filter((player) =>
      matchedToSession.some(
        (session) =>
          player.idfData.includes(session?.idfData as number) && session?.matchedToSession,
      ),
    )
  }, [groupedActivityDataList, selectedSession, sessionDataList, playerSessionDataMap])

  const selectedSessionDataList = useMemo(() => {
    return Object.entries(playerSessionDataMap || {}).reduce(
      (acc: Record<number, SessionData[]>, [sessionId, session]: [string, SessionData[]]) => {
        acc[Number(sessionId)] = session.filter(
          (sessionData) => sessionData.idfSession === selectedSession?.idfSession,
        )

        return acc
      },
      {} as Record<number, SessionData[]>,
    )
  }, [selectedSession, playerSessionDataMap])

  const handlePlayerScrollEvent = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop
    const timelineTarget = document.querySelector(".simplebar-content-wrapper") as HTMLElement

    if (timelineTarget) {
      timelineTarget.scrollTop = scrollTop
    }
  }

  const handleTimelineScrollEvent = (e: React.UIEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const scrollLeft = e.currentTarget.scrollLeft
    const scrollTop = e.currentTarget.scrollTop
    const timelineTarget = document.querySelector(".SessionTimelineContWrapper")
    const playerInfoTarget = playerInfoRef.current

    if (timelineTarget) {
      timelineTarget.scrollLeft = scrollLeft
    }

    if (playerInfoTarget) {
      playerInfoTarget.scrollTop = scrollTop
    }

    middleOfScrollBarSetter()
  }

  // The timeline itself doesn't scroll vertically (overflow-y: hidden); forward wheel to
  // player-device-info, which drives the timeline back via handlePlayerScrollEvent.
  const handleTimelineWheelEvent = (e: React.WheelEvent<HTMLDivElement>) => {
    const playerInfoTarget = playerInfoRef.current
    if (!playerInfoTarget) return
    // deltaMode 1 = lines, 2 = pages; normalize to pixels so mouse wheels aren't sluggish.
    const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? playerInfoTarget.clientHeight : 1
    playerInfoTarget.scrollTop += e.deltaY * factor
  }

  const getIsMatchedToSession = (groupData: ActivityDataTimelineProps) => {
    const sessionList = sessionDataList.flat()

    return groupData.idfData.map((idfData) => {
      return sessionList.find((sessionData: SessionData | undefined | null) => {
        return sessionData?.idfData === idfData
      })
    })
  }

  const onClickCopyButton = () => {
    setIsCopying(true)

    setLocalSessionCropHistory((prev) => [
      ...prev,
      tempSessionCropHistory.at(-1) as LocalSessionCropHistory,
    ])
  }

  const pastItemHeuristic = (data: LocalSessionCropData) => {
    const { idfData, idfSession, cropStartTime, cropEndTime } = data
    const dataList = playerSessionDataMap?.[idfData]
    const targetSession = dataList?.find((item) => item.idfSession === idfSession)

    if (targetSession) {
      let deleteHistory: LocalSessionCropHistory
      if (localSessionCropHistory.at(-2)?.actionType === CropType.delete) {
        deleteHistory = localSessionCropHistory.at(-2) as LocalSessionCropHistory
      } else {
        deleteHistory = {
          actionType: CropType.delete,
          history: [],
        } as LocalSessionCropHistory

        localSessionCropHistory.splice(localSessionCropHistory.length - 2, 0, deleteHistory)
      }

      let startDate = new Date(cropStartTime).valueOf()
      let endDate = new Date(cropEndTime).valueOf()

      targetSession.cropRecords.forEach((record) => {
        const { cropStartTime: recordedCropStartTime, cropEndTime: recordedCropEmdTime } = record
        const recordedStartDate = new Date(recordedCropStartTime).valueOf()
        const recordedEndDate = new Date(recordedCropEmdTime).valueOf()

        if (
          (recordedStartDate < endDate && recordedEndDate > endDate) ||
          (recordedStartDate < startDate && recordedEndDate > startDate) ||
          (recordedStartDate > startDate && recordedStartDate < endDate) ||
          (recordedEndDate > startDate && recordedEndDate < endDate)
        ) {
          deleteHistory.history.push({ ...record, idfData, idfSession })
          startDate = Math.min(startDate, recordedStartDate)
          endDate = Math.max(endDate, recordedEndDate)
          data.cropStartTime = new Date(startDate).toString()
          data.cropEndTime = new Date(endDate).toString()
        }
      })
    }

    setLocalSessionCropHistory([...localSessionCropHistory])
  }

  const onClickPastItem = (data: LocalSessionCropData) => {
    let lastHistory = localSessionCropHistory.at(-1) as LocalSessionCropHistory

    if (lastHistory && lastHistory.actionType === CropType.delete) {
      lastHistory = localSessionCropHistory.at(-2) as LocalSessionCropHistory
    }

    if (!lastHistory) {
      return
    }

    if (lastHistory.history) {
      lastHistory.history.push(data)
    }

    setLocalSessionCropHistory([...localSessionCropHistory])
  }

  const onClickPastAll = () => {
    setLocalSessionCropHistory([
      ...localSessionCropHistory,
      {
        localIdfCropNumber: `temp-${Date.now()}`,
        actionType: CropType.copyAll,
        history: [],
      } as LocalSessionCropHistory,
    ])
    // Apply All 은 그 자체로 종결 액션 — temp 선택을 비워서 DeviceCropTimeline 의 정리 effect
    // (tempCropDataList.length===0 && tempEndX) 가 발화되어 회색 박스/시간 라벨이 사라지도록 함.
    // tempCropStartDate/tempCropEndDate (별도 로컬 state) 는 보존되므로 copyAll useEffect 가 정상 동작.
    setTempSessionCropHistory([])
  }

  const onClickCancel = () => {
    const lastHistory = localSessionCropHistory.at(-1) as LocalSessionCropHistory

    if (lastHistory) {
      lastHistory.history.splice(1, lastHistory.history.length - 1)
    }

    setLocalSessionCropHistory([...localSessionCropHistory])
    setIsCopying(false)
    setLastActionType(() => CropType.cancel)
  }

  const opClickCrop = () => {
    if (isCopying) {
      setIsCopying(false)

      let lastHistory = localSessionCropHistory.at(-1) as LocalSessionCropHistory

      if (lastHistory && lastHistory.actionType === CropType.delete) {
        lastHistory = localSessionCropHistory.at(-2) as LocalSessionCropHistory
      }

      if (!lastHistory) {
        return
      }

      lastHistory.history.forEach(pastItemHeuristic)

      setTempSessionCropHistory([])

      return
    }

    const lastAction = localSessionCropHistory.at(-1)?.actionType
    if (lastAction === CropType.copyAll) {
      setTempSessionCropHistory([])

      return
    }

    setLocalSessionCropHistory([...localSessionCropHistory, ...tempSessionCropHistory])
    setTempSessionCropHistory([])
  }

  const onClickUndo = () => {
    if (isCopying) {
      onClickCancel()

      return
    }

    if (tempSessionCropHistory.length) {
      setTempSessionCropHistory((prev) => [...prev.slice(0, -1)])

      return
    }

    if (localSessionCropHistory.length) {
      setLocalSessionCropHistory((prev) => {
        let next = [...prev.slice(0, -1)]

        while (next.at(-1) && next.at(-1)?.actionType == CropType.delete) {
          next = [...next.slice(0, -1)]
        }

        return next
      })
    }
  }

  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (isDirty.current) {
      e.preventDefault()
      e.returnValue = ""
    }
  }

  const beforeRouterChange = () => {
    if (isDirty.current && !window.confirm(t("messages.confirmLeavePage"))) {
      return true
    }

    return false
  }

  useEffect(() => {
    isDirty.current = !!localSessionCropHistory.length || !!tempSessionCropHistory.length
  }, [localSessionCropHistory, tempSessionCropHistory])

  useEffect(() => {
    const lastAction = localSessionCropHistory.at(-1)?.actionType

    setLastActionType(lastAction)
  }, [localSessionCropHistory.at(-1)?.actionType])

  useEffect(() => {
    if (router) {
      router.addEventListener(RouterEvent.routeChangeStart, beforeRouterChange)
      window.addEventListener("beforeunload", handleBeforeUnload)

      return () => {
        router.removeEventListener(RouterEvent.routeChangeStart, beforeRouterChange)
        window.removeEventListener("beforeunload", handleBeforeUnload)
      }
    }
  }, [router])

  return (
    <div className="activity-session-section activity-crop-mode">
      <ActivityDeviceInfo isCropping />
      <div className="session-timeline-wrapper">
        <SessionCropTimeline
          isLoading={!sessionList}
          currentItemList={sessionList || []}
          dateStart={sessionDateStart}
          dateFinish={sessionDateFinish}
          height={183}
          onBoxClick={(session) => {
            if (selectedSession?.idfSession === (session as Session).idfSession) {
              setSession(undefined)

              return
            }
            setSession(session as Session)
          }}
        />
        <SessionCropTaskBar
          isSelectingCopyItem={isCopying}
          isUndoBtnDisabled={
            tempSessionCropHistory.length === 0 && localSessionCropHistory.length === 0
          }
          isCropBtnDisabled={tempSessionCropHistory.length === 0}
          onClickCopy={onClickCopyButton}
          onClickCancel={onClickCancel}
          onClickCrop={opClickCrop}
          onClickUndo={onClickUndo}
          onClickApplyAll={onClickPastAll}
        />
      </div>

      <div className="player-device-info" ref={playerInfoRef} onScroll={handlePlayerScrollEvent}>
        {groupedActivityData?.map((groupData) => {
          const isMatchedToSessionFlags = getIsMatchedToSession(groupData)
          const idfData = groupData.idfData.filter((_, index) => {
            return isMatchedToSessionFlags[index]?.matchedToSession
          })

          const sessionDataList = groupData.idfData
            .map((idfData) => selectedSessionDataList[idfData])
            .flat()
            .filter((sessionData) => sessionData?.matchedToSession)

          return sessionDataList.map((sessionData, idfDataIdx) => (
            <div className="player-device-info-inner" key={sessionData.idfData + "device"}>
              <DevicePlayerInfo
                key={sessionData.idfData}
                className={classNames("device-player", {
                  matchedToActivity: groupData.matchedToActivity,
                })}
                activityData={{
                  ...groupData,
                  idfData: sessionData.idfData,
                  position: getPlayerPosition(groupData.idfPlayer, groupData.position),
                  devicePlayer: {
                    ...groupData.devicePlayer,
                    nickname:
                      (groupData.devicePlayer?.nickname || "") +
                      (idfData.length > 1 && groupData.devicePlayer ? ` (${idfDataIdx + 1})` : ""),
                  },
                }}
                teamDeviceList={teamDeviceList}
                playerGroupName={getPlayerGroupName(groupData.idfPlayer)}
                isCropping
              />
            </div>
          ))
        })}
      </div>

      <OverlayScrollbar
        className="activity-device-timeline"
        autoHide
        ref={activityTimelineContRef}
        scrollableNodeProps={{
          onScroll: handleTimelineScrollEvent,
          onWheel: handleTimelineWheelEvent,
        }}>
        {groupedActivityData?.map((groupData, index) => {
          const sessionDataList = groupData.idfData
            .map((idfData) => selectedSessionDataList[idfData])
            .flat()
            .filter((sessionData) => sessionData?.matchedToSession)

          return sessionDataList.map((sessionData) => (
            <div
              className="player-device-timeline"
              key={sessionData.idfData + "_" + index + "-device-timeline"}>
              <div
                className="device-timeline"
                key={`${groupData.idfPlayer}-${sessionData}-timeline`}>
                <div className="device-timeline-guideline-cont">
                  <DeviceCropTimeline
                    className={classNames({ matchedToActivity: groupData.matchedToActivity })}
                    key={groupData.idfData + "-" + index}
                    activityData={{
                      ...groupData,
                      idfData: sessionData.idfData,
                      dateFinish: sessionData.dateFinish,
                      dateStart: sessionData.dateStart,
                    }}
                    localCropDataList={localSessionCropHistory}
                    tempCropDataList={tempSessionCropHistory}
                    idfSession={sessionData.idfSession}
                    isCopying={isCopying}
                    sessionCropDataList={sessionData.cropRecords.filter(
                      (item) => !item.localIdfCropNumber,
                    )}
                    lastCropAction={lastActionType}
                    addHistoryStack={(data: LocalSessionCropHistory) => {
                      setTempSessionCropHistory((prev) => [...prev, data])
                    }}
                    popHistoryStack={() => {
                      setTempSessionCropHistory((prev) => [...prev.slice(0, -1)])
                    }}
                    popHistoryStackDirect={() => {
                      setLocalSessionCropHistory((prev) => [...prev.slice(0, -1)])
                    }}
                    addCropDataToLastHistory={onClickPastItem}
                    setDeleteHistory={setDeleteHistory}
                  />
                </div>
              </div>
            </div>
          ))
        })}
      </OverlayScrollbar>
      <CropDeleteConfirmDialog
        isOpen={deleteHistory !== undefined}
        data={deleteHistory as LocalSessionCropHistory}
        onConfirm={(data) => {
          const cropToDelete = data.history[0]

          const tempIndex = tempSessionCropHistory.findIndex((item) =>
            item.history.some(
              (h) =>
                h.idfData === cropToDelete.idfData &&
                ((h.localIdfCropNumber &&
                  cropToDelete.localIdfCropNumber &&
                  h.localIdfCropNumber === cropToDelete.localIdfCropNumber) ||
                  (h.idfSessionCropRecord &&
                    h.idfSessionCropRecord !== 0 &&
                    cropToDelete.idfSessionCropRecord &&
                    cropToDelete.idfSessionCropRecord !== 0 &&
                    h.idfSessionCropRecord === cropToDelete.idfSessionCropRecord)),
            ),
          )

          if (tempIndex !== -1) {
            setTempSessionCropHistory((prev) => {
              const newHistory = [...prev]
              const targetItem = newHistory[tempIndex]

              targetItem.history = targetItem.history.filter(
                (h) =>
                  !(
                    h.idfData === cropToDelete.idfData &&
                    ((h.localIdfCropNumber &&
                      cropToDelete.localIdfCropNumber &&
                      h.localIdfCropNumber === cropToDelete.localIdfCropNumber) ||
                      (h.idfSessionCropRecord &&
                        h.idfSessionCropRecord !== 0 &&
                        cropToDelete.idfSessionCropRecord &&
                        cropToDelete.idfSessionCropRecord !== 0 &&
                        h.idfSessionCropRecord === cropToDelete.idfSessionCropRecord))
                  ),
              )

              if (targetItem.history.length === 0) {
                return newHistory.filter((_, index) => index !== tempIndex)
              }

              return newHistory
            })
          } else {
            setLocalSessionCropHistory((prev) => [...prev, data])
          }

          setDeleteHistory(undefined)
        }}
        onCancel={() => setDeleteHistory(undefined)}
      />
    </div>
  )
}
